import React, { useEffect, useState } from 'react';
import { X, CreditCard, CirclePlus, Loader2 } from 'lucide-react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import AddCardDetails from './AddCardDetails';
import DeleteCreditCard from './DeleteCreditCard';
import CardSelector from '../../pages/profile/elements/CardSelector';
import { updateSubscriptionPayment } from '../../store/thunk/ordersThunk';
import { fetchUserPayments } from '../../store/thunk/authThunk';

const SubscriptionPaymentModal = ({ open, setOpen, paymentCardId, subscriptionId, onUpdate }) => {
  const dispatch = useDispatch();
  const { userPayments} = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isCardDetails, setIsCardDetails] = useState(false);
  const [editCardData, setEditCardData] = useState(null);
  const [isDeleteCardModal, setDeleteCardModal] = useState(false);
  const [deleteCreditCard, setDeleteCreditCard] = useState({});
  useEffect(() => {
    if (paymentCardId) {
      setSelectedCardId(paymentCardId);
    }
  }, [paymentCardId]);

  // Fetch payments when modal opens if not already loaded
  useEffect(() => {
    setPaymentLoading(true);
    if (open && !userPayments?.payment_card_info) {
      dispatch(fetchUserPayments());
    }else{
      setPaymentLoading(false);
    }
  }, [open, dispatch, userPayments]);

  const handleSelectCard = (id) => {
    setSelectedCardId(id);
  };

  const handleEditCard = (cardData) => {
    setEditCardData(cardData);
    setIsCardDetails(true);
  };

  const handleDeleteCard = (card) => {
    setDeleteCardModal(true);
    setDeleteCreditCard(card);
  };

  const handleAddNewCard = () => {
    setIsCardDetails(true);
    setEditCardData(null);
  };

  const handleCardAdded = () => {
    setIsCardDetails(false);
  };

  const handleCardUpdated = () => {
    setIsCardDetails(false);
    setEditCardData(null);
  };

  const handleCardDeleted = () => {
    setDeleteCardModal(false);
    setDeleteCreditCard({});
    if (deleteCreditCard.payment_id === selectedCardId) {
      setSelectedCardId(null);
    }
  };

  const handleUpdatePayment = async () => {
    if (!selectedCardId) {
      toast.error('Please select a credit card');
      return;
    }

    setLoading(true);

    try {
      const result = await dispatch(updateSubscriptionPayment({
        subscriptionId,
        payment_method: 'credit-card',
        payment_id: selectedCardId
      })).unwrap();

      if (result?.status === 1) {
        toast.success(result?.message || 'Payment method updated successfully!');

        if (onUpdate) {
          onUpdate();
        }

        setTimeout(() => {
          setOpen(false);
        }, 1000);
      } else {
        toast.error(result?.message || 'Failed to update payment method');
      }
    } catch (error) {
      console.error('Failed to update payment method:', error);
      toast.error(error?.message || 'Failed to update payment method');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    setSelectedCardId(paymentCardId || null);
  };

  const hasChanges = () => {
    return selectedCardId !== paymentCardId;
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} className='relative z-50'>
        <DialogBackdrop className='fixed inset-0 bg-gray-500/75 transition-opacity' />
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          <DialogPanel className='bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col'>
            {/* Header - Fixed */}
            <div className='flex items-baseline justify-between bg-red-gradient p-4 flex-shrink-0'>
              <DialogTitle className='text-lg font-semibold letter-spacing-1 text-white flex items-center space-x-3'>
                <CreditCard size={25} className='mr-3' />
                Update Payment Method
              </DialogTitle>
              <button
                onClick={handleClose}
                className='text-white hover:text-gray-200 transition-colors'
                disabled={loading}
              >
                <X size={24} />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className='overflow-y-auto flex-1'>
              <div className='p-5'>
                <div className="mb-6">
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <span className="text-md font-semibold text-gray-800">
                      Credit Card
                    </span>
                    <button
                      onClick={handleAddNewCard}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-100 hover:text-red-700 transition-colors"
                    >
                      <CirclePlus size={18} />
                      Add New Card
                    </button>
                  </div>

                  <div className="space-y-4">
                    {paymentLoading ? (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <Loader2 className="w-8 h-8 animate-spin mb-3" />
                        <p className="text-sm">Loading payment methods...</p>
                      </div>
                    ) : !userPayments?.payment_card_info || userPayments?.payment_card_info?.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        <h4 className="text-md mb-4">No credit cards available</h4>
                        <button
                          onClick={handleAddNewCard}
                          className="main-btn text-sm px-10 py-2 hover:shadow-lg transition-shadow"
                        >
                          Add new card
                        </button>
                      </div>
                    ) : (
                      userPayments?.payment_card_info?.map((card) => (
                        <CardSelector
                          key={card.payment_id}
                          card={card}
                          selectedCardId={selectedCardId}
                          onSelect={handleSelectCard}
                          onEdit={handleEditCard}
                          onDelete={handleDeleteCard}
                          className="border-b shadow-[0_3px_10px_rgb(0,0,0,0.2)] pb-3 rounded-2xl"
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className='border-t px-5 py-4 flex justify-center flex-shrink-0'>
              <button
                onClick={handleUpdatePayment}
                disabled={loading || !hasChanges()}
                className={`main-btn py-2 mx-auto w-fit px-10 transition-all duration-200 ${
                  loading || !hasChanges()
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <div className='flex items-center space-x-2'>
                    <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                    <span>Updating...</span>
                  </div>
                ) : (
                  <div className='flex items-center space-x-2 letter-spacing-1 font-semibold'>
                    <CreditCard size={26} />
                    <span>Update Payment Method</span>
                  </div>
                )}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Add/Edit Card Modal */}
      <AddCardDetails
        open={isCardDetails}
        setOpen={setIsCardDetails}
        editCardData={editCardData}
        onCardAdded={handleCardAdded}
        onCardUpdated={handleCardUpdated}
      />

      {/* Delete Card Modal */}
      <DeleteCreditCard
        open={isDeleteCardModal}
        setOpen={setDeleteCardModal}
        creditCard={deleteCreditCard}
        onCardDeleted={handleCardDeleted}
      />
    </>
  );
};

export default SubscriptionPaymentModal;
